import { useTranslation } from 'react-i18next';
import { Button, PanelSection, Textarea } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { PersonSelectorField } from '@/modules/planner/components/PersonSelector';
import { RESPONSIBILITY_ROLE_FIELDS } from '@/modules/planner/responsibleRoles';
import { TRANSPORT_TYPE_OPTIONS } from '../serieFormSchema';
import type { SerieFormApi } from '../useSerieForm';

interface SerieFormFieldsProps {
  form: SerieFormApi;
}

/**
 * Sdílená sada polí pro založení série (Název série, Typ dopravy, Odpovědné osoby, Interní poznámka).
 * Používá ji jak formulář série, tak formulář produktu (ten navíc nad ní renderuje Název produktu).
 */
export function SerieFormFields({ form }: SerieFormFieldsProps) {
  const { t } = useTranslation('planner');

  return (
    <>
      <PanelSection>
        <form.Field name="name">
          {(field) => (
            <TextField
              field={field}
              label={t('serie.name.label')}
              required
              helperText={t('serie.name.help')}
              placeholder={t('serie.name.placeholder')}
              data-testid="serie-name-input"
            />
          )}
        </form.Field>
      </PanelSection>

      <PanelSection title={t('serie.transport.sectionLabel')}>
        <p className="text-phx-text-muted text-phx-xs">{t('serie.transport.help')}</p>
        <form.Field name="transportType">
          {(field) => (
            <div className="flex flex-wrap gap-1" role="group" data-testid="serie-transport-type">
              {TRANSPORT_TYPE_OPTIONS.map((option) => (
                <Button
                  key={option}
                  type="button"
                  size="sm"
                  variant={field.state.value === option ? 'primary' : 'outline'}
                  onClick={() => field.handleChange(option)}
                >
                  {t(`serie.transport.${option}`)}
                </Button>
              ))}
            </div>
          )}
        </form.Field>
      </PanelSection>

      <PanelSection title={t('serie.persons.sectionLabel')}>
        <p className="text-phx-text-muted text-phx-xs">{t('serie.persons.help')}</p>
        <div className="gap-x-phx-4 gap-y-phx-3 grid grid-cols-1 md:grid-cols-2">
          {RESPONSIBILITY_ROLE_FIELDS.map((roleField) => (
            <form.Field key={roleField.role} name={`responsiblePersons.${roleField.key}`}>
              {(field) => (
                <PersonSelectorField
                  field={field}
                  mode="single"
                  label={t(`project.roles.${roleField.key}`)}
                  placeholder={t('serie.persons.placeholder')}
                  testId={`serie-person-${roleField.key}`}
                />
              )}
            </form.Field>
          ))}
        </div>
      </PanelSection>

      <PanelSection title={t('serie.note.sectionLabel')}>
        <form.Field name="note">
          {(field) => (
            <Textarea
              rows={5}
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
              placeholder={t('serie.note.placeholder')}
              data-testid="serie-note-input"
            />
          )}
        </form.Field>
      </PanelSection>
    </>
  );
}
